import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Banknote, Paperclip, FileText } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import {
  listarRecebimentos, lancarRecebimento, apagarRecebimento, listarContas,
  saldoDaParcela, situacaoDaParcela, custoFinanceiro,
} from '../../../data/recebimentos';
import {
  anexarComprovante, removerComprovante, urlsDosComprovantes, problemaDoTipo,
} from '../../../data/comprovantes';
import { alterarVencimento } from '../../../data/invoices';
import { FORMAS_DE_PAGAMENTO, rotuloDaForma, MARK } from '../../../constants';

const input = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR',
  { style: 'currency', currency: 'BRL' });

const dataCurta = (iso) => {
  const [a, m, d] = String(iso || '').slice(0, 10).split('-');
  return a ? `${d}/${m}/${a}` : '—';
};

const Campo = ({ label, children, dica }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    {children}
    {dica && <p className="text-xs text-slate-400 mt-0.5">{dica}</p>}
  </div>
);

/**
 * Lançamentos de recebimento de UMA parcela.
 *
 * Substitui o botão que marcava pago com um clique. A diferença que importa:
 * a parcela passa a aceitar vários lançamentos, com valor, meio, data e conta
 * — e "quitada" vira consequência da soma, não de alguém ter clicado.
 *
 * ANTECIPAÇÃO MARCA COMO PAGO. O cliente pagou o valor cheio; o que entrou na
 * conta foi menos. A diferença é despesa financeira do laboratório, e aparece
 * como tal — não como saldo que o cliente deve.
 */
const RecebimentoModal = ({ show, parcela: parcelaRecebida, servico, laboratoryId, onFechar, onMudou }) => {
  /*
   * O VENCIMENTO SE EDITA AQUI, onde ele já aparece. A parcela vem de fora e
   * não se recarrega sozinha: a cópia local é o que faz o subtítulo e o campo
   * mostrarem a data nova logo depois de salvar.
   */
  const [parcela, setParcela] = useState(parcelaRecebida);
  useEffect(() => { setParcela(parcelaRecebida); }, [parcelaRecebida]);
  const [vencimento, setVencimento] = useState('');
  useEffect(() => { setVencimento(String(parcela?.vencimento || '').slice(0, 10)); }, [parcela?.vencimento]);
  const [salvandoData, setSalvandoData] = useState(false);
  const [lancamentos, setLancamentos] = useState([]);
  // Caminho no bucket -> endereço assinado. Assinado ao carregar, e não no
  // clique: ver a explicação em `urlsDosComprovantes`.
  const [comprovantes, setComprovantes] = useState({});
  // Id do lançamento cujo arquivo está subindo, ou 'novo' para o do formulário.
  const [anexando, setAnexando] = useState(null);
  const [contas, setContas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [f, setF] = useState({
    valor: '', valor_liquido: '', meio: '', conta_id: '',
    antecipado: false, recebido_em: new Date().toISOString().slice(0, 10), observacao: '',
  });
  // O arquivo escolhido antes de lançar. Só sobe depois que a linha existe —
  // o caminho dele leva o id do recebimento.
  const [arquivoNovo, setArquivoNovo] = useState(null);
  // `<input type="file">` não se limpa por estado. A chave muda depois de
  // gravar e o campo nasce de novo, vazio.
  const [chaveDoCampo, setChaveDoCampo] = useState(0);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const carregar = useCallback(async () => {
    if (!parcela?.id) return;
    setCarregando(true); setErro('');
    try {
      const [l, c] = await Promise.all([
        listarRecebimentos([parcela.id]),
        listarContas(laboratoryId),
      ]);
      setLancamentos(l || []);
      setContas((c || []).filter(x => x.ativa));
      setComprovantes(await urlsDosComprovantes((l || []).map(r => r.comprovante_path)));
    } catch (e) {
      console.error('recebimentos:', e); setErro(e.message);
    } finally { setCarregando(false); }
  }, [parcela?.id, laboratoryId]);

  useEffect(() => { if (show) carregar(); }, [show, carregar]);

  // Sugere o que falta, que é o lançamento mais comum. Editável: recebimento
  // parcial é justamente o que esta tela veio permitir.
  const falta = saldoDaParcela(parcela, lancamentos);
  useEffect(() => {
    if (show && !carregando) setF(p => ({ ...p, valor: falta > 0 ? String(falta) : '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, carregando, falta]);

  if (!show || !parcela) return null;

  const situacao = situacaoDaParcela(parcela, lancamentos);
  const custo = custoFinanceiro(lancamentos);
  const valorNovo = Number(f.valor) || 0;
  const liquidoNovo = f.valor_liquido === '' ? valorNovo : Number(f.valor_liquido) || 0;

  /**
   * O que o formulário recusa, e por quê.
   *
   * Recebia qualquer valor positivo. Duas portas ficavam abertas:
   *
   *   - RECEBER MAIS QUE O DEVIDO. O excedente não aparecia em lugar nenhum:
   *     o saldo é limitado a zero na tela e na view, mas o "recebido" contava
   *     tudo — o painel mostrava recebido maior que faturado com saldo zero, e
   *     ninguém sabia de onde vinha a diferença.
   *   - LÍQUIDO MAIOR QUE O VALOR. Produz custo financeiro negativo, isto é,
   *     o laboratório teria ganho dinheiro na taxa do banco.
   *
   * A do excedente é um AVISO, não um bloqueio: juros e multa chegam como
   * valor acima da parcela, e recusá-los travaria o lançamento verdadeiro. A
   * do líquido é bloqueio, porque não existe caso legítimo.
   */
  const excede = valorNovo > 0 && valorNovo - falta > 0.005;
  const liquidoInvalido = liquidoNovo - valorNovo > 0.005;
  const pode = valorNovo > 0 && f.recebido_em && !liquidoInvalido && !salvando;

  // Só o TIPO na hora de escolher: o tamanho se decide depois da compressão,
  // e uma foto de 15 MB que vira 300 KB não pode ser recusada na porta.
  const escolherArquivo = (arquivo) => {
    const problema = arquivo ? problemaDoTipo(arquivo) : null;
    if (problema) { setErro(problema); setArquivoNovo(null); return; }
    setErro(''); setArquivoNovo(arquivo);
  };

  const gravar = async () => {
    setSalvando(true); setErro('');
    // O aviso do comprovante é dado DEPOIS de recarregar: `carregar` limpa o
    // erro ao começar, e escrito antes ele sumiria sem ninguém ler.
    let aviso = '';
    try {
      const novo = await lancarRecebimento({
        laboratory_id:  laboratoryId,
        installment_id: parcela.id,
        recebido_em:    f.recebido_em,
        valor:          valorNovo,
        // Só grava o líquido quando ele DIFERE: igual seria ruído, e nulo já
        // significa "entrou tudo".
        valor_liquido:  f.valor_liquido === '' || liquidoNovo === valorNovo ? null : liquidoNovo,
        meio:           f.meio || null,
        conta_id:       f.conta_id || null,
        antecipado:     !!f.antecipado,
        observacao:     f.observacao?.trim() || null,
      });
      /**
       * O arquivo sobe DEPOIS da linha, porque o caminho dele leva o id do
       * recebimento.
       *
       * Falha de upload não desfaz o lançamento: o dinheiro entrou e o registro
       * dele é o que importa. Mas o aviso precisa dizer as duas coisas na mesma
       * frase — "erro ao anexar" sozinho parece que nada foi salvo, e a pessoa
       * lança de novo. Aí a parcela recebe em dobro.
       */
      if (arquivoNovo && novo?.id) {
        try {
          await anexarComprovante({ laboratoryId, recebimentoId: novo.id, arquivo: arquivoNovo });
        } catch (e) {
          console.error('comprovante:', e);
          aviso = `O recebimento de ${dinheiro(valorNovo)} FOI lançado, mas o comprovante não `
            + `subiu (${e.message}). Anexe pela lista abaixo — não lance de novo.`;
        }
      }
      setArquivoNovo(null);
      setChaveDoCampo(n => n + 1);
      setF(p => ({ ...p, valor_liquido: '', observacao: '', antecipado: false }));
      await carregar();
      if (aviso) setErro(aviso);
      onMudou?.();
    } catch (e) {
      console.error('lançar recebimento:', e); setErro(e.message);
    } finally { setSalvando(false); }
  };

  /** Anexar a um lançamento que já existe — o caminho de quem achou o print depois. */
  const anexar = async (r, arquivo) => {
    const problema = arquivo ? problemaDoTipo(arquivo) : 'Nenhum arquivo escolhido.';
    if (problema) { setErro(problema); return; }
    setAnexando(r.id); setErro('');
    try {
      await anexarComprovante({ laboratoryId, recebimentoId: r.id, arquivo });
      await carregar();
    } catch (e) {
      console.error('comprovante:', e); setErro(e.message);
    } finally { setAnexando(null); }
  };

  const tirarComprovante = async (r) => {
    if (!window.confirm(
      `Remover o comprovante deste recebimento?\n\n`
      + 'O lançamento continua como está: sai só o arquivo.')) return;
    try {
      await removerComprovante(r.id, r.comprovante_path);
      await carregar();
    } catch (e) { setErro(e.message); }
  };

  const vencimentoAtual = String(parcela.vencimento || '').slice(0, 10);
  const dataMudou = vencimento && vencimento !== vencimentoAtual;

  const salvarVencimento = async () => {
    setSalvandoData(true); setErro('');
    try {
      const nova = await alterarVencimento(parcela.id, vencimento);
      setParcela(p => ({ ...p, vencimento: nova?.vencimento || vencimento }));
      onMudou?.();
    } catch (e) {
      console.error('vencimento:', e); setErro(e.message);
    } finally { setSalvandoData(false); }
  };

  const remover = async (r) => {
    if (!window.confirm(
      `Apagar o recebimento de ${dinheiro(r.valor)} de ${dataCurta(r.recebido_em)}?\n\n`
      + 'A parcela volta a contar como não quitada, e o saldo do cliente sobe de novo.')) return;
    try {
      await apagarRecebimento(r.id);
      await carregar();
      onMudou?.();
    } catch (e) { setErro(e.message); }
  };

  return (
    <JanelaModal
      chave="recebimentos"
      Icone={Banknote}
      titulo={<>Recebimentos — {parcela.total > 1 ? `${parcela.numero}ª parcela` : 'parcela única'}</>}
      subtitulo={<>
        {servico?.client_name}
        {servico?.numero ? ` · serviço #${servico.numero}` : ''}
        {' · vence '}{dataCurta(parcela.vencimento)}
      </>}
      onFechar={onFechar}
      largura="sm:max-w-3xl"
    >
        {/* O corpo rola e o "Fechar" do rodapé fica à vista. */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Os três números que a tela existe para responder. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 border border-slate-200 rounded p-3">
            <div>
              <p className="text-xs text-slate-500">Valor da parcela</p>
              <p className="font-semibold text-slate-900">{dinheiro(parcela.valor)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Já recebido</p>
              <p className="font-semibold text-slate-900">
                {dinheiro(lancamentos.reduce((s, r) => s + Number(r.valor || 0), 0))}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Falta</p>
              <p className="font-semibold text-slate-900">
                {dinheiro(falta)}
                {situacao === 'quitada' && <span> {MARK.OK}</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Custo financeiro</p>
              <p className="font-semibold text-slate-900">{dinheiro(custo)}</p>
              {custo > 0 && (
                <p className="text-xs text-slate-400">taxa e antecipação</p>
              )}
            </div>
          </div>

          {/* ── Vencimento ── */}
          <div className="flex flex-wrap items-end gap-2">
            <Campo label="Vencimento"
              dica={situacao === 'quitada' ? 'Parcela quitada: a data não muda a cobrança.' : ''}>
              <input className={input} type="date" value={vencimento}
                onChange={(e) => setVencimento(e.target.value)} />
            </Campo>
            {dataMudou && (
              <>
                <button onClick={salvarVencimento} disabled={salvandoData}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40">
                  {salvandoData ? 'Salvando…' : 'Salvar data'}
                </button>
                <button onClick={() => setVencimento(vencimentoAtual)} disabled={salvandoData}
                  className="px-3 py-1.5 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
                  Desfazer
                </button>
              </>
            )}
          </div>

          {/* ── Lançar ── */}
          <div className="border border-slate-200 rounded p-3 space-y-3">
            <p className="text-xs font-medium text-slate-700">Novo recebimento</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Campo label="Valor recebido *" dica="O que o cliente pagou">
                <input className={input} type="number" step="0.01" min="0.01" value={f.valor}
                  onChange={(e) => set('valor', e.target.value)} />
              </Campo>
              <Campo label="Data *">
                <input className={input} type="date" value={f.recebido_em}
                  onChange={(e) => set('recebido_em', e.target.value)} />
              </Campo>
              <Campo label="Meio">
                <select className={input} value={f.meio} onChange={(e) => set('meio', e.target.value)}>
                  <option value="">Não informado</option>
                  {FORMAS_DE_PAGAMENTO.map(m => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </Campo>
              <Campo label="Conta destino"
                dica={contas.length === 0 ? 'Cadastre em Laboratório' : ''}>
                <select className={input} value={f.conta_id}
                  onChange={(e) => set('conta_id', e.target.value)}>
                  <option value="">Não informada</option>
                  {contas.map(c => (
                    <option key={c.id} value={c.id}>{c.apelido}</option>
                  ))}
                </select>
              </Campo>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={f.antecipado}
                onChange={(e) => set('antecipado', e.target.checked)}
                className="rounded border-slate-300" />
              Antecipado
            </label>

            {/* O líquido só aparece quando faz diferença. Pedir sempre faria a
                pessoa digitar o mesmo número duas vezes. */}
            {f.antecipado && (
              <Campo label="Valor que entrou na conta"
                dica="Depois da taxa. A parcela quita pelo valor cheio — a diferença é despesa sua, não dívida do cliente.">
                <input className={input} type="number" step="0.01" min="0"
                  placeholder={String(valorNovo || '')}
                  value={f.valor_liquido}
                  onChange={(e) => set('valor_liquido', e.target.value)} />
              </Campo>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Campo label="Observação">
                <input className={input} value={f.observacao}
                  onChange={(e) => set('observacao', e.target.value)} />
              </Campo>

              {/* O COMPROVANTE ENTRA NO MESMO GESTO do lançamento: quem acabou
                  de receber o pix tem o print na mão, e é agora que ele custa
                  um toque. Anexar depois continua possível na lista abaixo,
                  mas depende de alguém lembrar — e três meses depois, quando o
                  cliente pergunta, ninguém lembra. */}
              <Campo label="Comprovante"
                dica={arquivoNovo
                  ? `${arquivoNovo.name} — sobe junto com o lançamento`
                  : 'Foto ou PDF do pix — a foto é reduzida antes de subir'}>
                <input
                  key={chaveDoCampo}
                  type="file"
                  accept="image/*,application/pdf"
                  className="block w-full text-sm text-slate-600 file:mr-2 file:py-1 file:px-2
                             file:rounded file:border file:border-slate-300 file:text-xs
                             file:bg-white file:text-slate-700 hover:file:bg-slate-50"
                  onChange={(e) => escolherArquivo(e.target.files?.[0] || null)} />
              </Campo>
            </div>

            {f.antecipado && valorNovo > 0 && liquidoNovo < valorNovo && (
              <p className="text-xs text-slate-600">
                {MARK.WARN} A parcela fica quitada por {dinheiro(valorNovo)}, e{' '}
                {dinheiro(valorNovo - liquidoNovo)} entram como custo financeiro.
              </p>
            )}

            {liquidoInvalido && (
              <p className="text-xs text-slate-700">
                {MARK.WARN} O líquido não pode ser maior que o valor recebido. O líquido é o que
                entrou na conta depois da taxa, então ele é sempre menor ou igual.
              </p>
            )}

            {excede && !liquidoInvalido && (
              <p className="text-xs text-slate-600">
                {MARK.WARN} Faltam {dinheiro(falta)} nesta parcela e você está lançando{' '}
                {dinheiro(valorNovo)}. A diferença de {dinheiro(valorNovo - falta)} não vira
                crédito em lugar nenhum: o saldo apenas para em zero. Se for juro ou multa,
                registre na observação.
              </p>
            )}

            <button onClick={gravar} disabled={!pode}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40">
              {salvando ? 'Lançando…' : 'Lançar recebimento'}
            </button>
          </div>

          {erro && (
            <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded px-3 py-2">
              {erro}
            </div>
          )}

          {/* ── O que já entrou ── */}
          {carregando ? (
            <p className="text-sm text-slate-500 text-center py-4">Carregando…</p>
          ) : lancamentos.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              Nenhum recebimento lançado nesta parcela.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Data</th>
                    <th className="px-2 py-1.5 text-right font-medium">Cliente pagou</th>
                    <th className="px-2 py-1.5 text-right font-medium">Entrou na conta</th>
                    <th className="px-2 py-1.5 text-left font-medium">Meio</th>
                    <th className="px-2 py-1.5 text-left font-medium">Conta</th>
                    <th className="px-2 py-1.5 text-left font-medium">Comprovante</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lancamentos.map(r => {
                    const liquido = r.valor_liquido == null ? Number(r.valor) : Number(r.valor_liquido);
                    const conta = contas.find(c => c.id === r.conta_id);
                    return (
                      <tr key={r.id}>
                        <td className="px-2 py-1.5 text-slate-700">{dataCurta(r.recebido_em)}</td>
                        <td className="px-2 py-1.5 text-right font-medium text-slate-900">
                          {dinheiro(r.valor)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-slate-700">
                          {dinheiro(liquido)}
                          {liquido < Number(r.valor) && (
                            <span className="block text-xs text-slate-400">
                              −{dinheiro(Number(r.valor) - liquido)}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">
                          {rotuloDaForma(r.meio) || '—'}
                          {r.antecipado && (
                            <span className="block text-xs text-slate-400">antecipado</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{conta?.apelido || '—'}</td>
                        {/* Três estados, e cada um tem uma ação só: tem e abre,
                            tem e não assinou agora, não tem e anexa. */}
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {r.comprovante_path ? (
                            <span className="inline-flex items-center gap-2">
                              {comprovantes[r.comprovante_path] ? (
                                <a
                                  href={comprovantes[r.comprovante_path]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Abrir o comprovante"
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                >
                                  <FileText size={12} /> Ver
                                </a>
                              ) : (
                                <span className="text-xs text-slate-500"
                                  title="O arquivo está anexado, mas não deu para abrir agora. Feche e abra a tela de novo.">
                                  anexado
                                </span>
                              )}
                              <button onClick={() => tirarComprovante(r)}
                                className="text-xs text-slate-400 hover:text-slate-700">
                                remover
                              </button>
                            </span>
                          ) : (
                            <label className="inline-flex items-center gap-1 text-xs text-slate-500
                                              hover:text-slate-800 cursor-pointer">
                              <Paperclip size={12} />
                              {anexando === r.id ? 'Enviando…' : 'Anexar'}
                              <input type="file" accept="image/*,application/pdf" className="hidden"
                                disabled={anexando === r.id}
                                onChange={(e) => anexar(r, e.target.files?.[0] || null)} />
                            </label>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button onClick={() => remover(r)}
                            className="p-1 text-slate-400 hover:text-slate-700 rounded">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button onClick={onFechar}
            className="px-4 py-2 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
            Fechar
          </button>
        </div>
    </JanelaModal>
  );
};

export default RecebimentoModal;
